import { TypedLocale } from 'payload'

/**
 * Centralized Query Key Management
 *
 * This module provides a type-safe, centralized API for all query keys used
 * with Tanstack Query. Using this system ensures consistency, prevents typos,
 * and provides IDE autocomplete for cache operations.
 *
 * @example
 * ```typescript
 * // Product groups query
 * const { data } = useQuery({
 *   queryKey: queryKeys.productGroups.list(locale, where, page, limit),
 *   queryFn: () => fetchProductGroupsData({ ... })
 * })
 *
 * // Invalidate all product groups queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.productGroups.all })
 * ```
 */
export const queryKeys = {
  /**
   * Product groups query keys
   */
  productGroups: {
    /** Base key for all product groups queries */
    all: ['product-groups'] as const,

    /** Query key for product groups list with filters */
    list: (
      locale: TypedLocale,
      where: Record<string, any>,
      page?: number,
      limit?: number,
    ) => [
      ...queryKeys.productGroups.all,
      'list',
      locale,
      where,
      page ?? 1,
      limit ?? 10,
    ] as const,
  },

  /**
   * Solutions query keys (for future use)
   */
  solutions: {
    /** Base key for all solutions queries */
    all: ['solutions'] as const,

    /** Query key for solutions list with filters */
    list: (
      locale: TypedLocale,
      where: Record<string, any>,
      page?: number,
      limit?: number,
    ) => [
      ...queryKeys.solutions.all,
      'list',
      locale,
      where,
      page ?? 1,
      limit ?? 10,
    ] as const,
  },

  /**
   * Products query keys (for future use)
   */
  products: {
    /** Base key for all products queries */
    all: ['products'] as const,

    /** Query key for products list with filters */
    list: (
      locale: TypedLocale,
      where: Record<string, any>,
      page?: number,
      limit?: number,
    ) => [
      ...queryKeys.products.all,
      'list',
      locale,
      where,
      page ?? 1,
      limit ?? 10,
    ] as const,
  },

  /**
   * Collection archive query keys (generic archive component)
   */
  collectionArchive: {
    /** Base key for all collection archive queries */
    all: ['collection-archive'] as const,

    /** Query key scoped to a specific archive instance */
    byArchive: (archiveId: string) =>
      [...queryKeys.collectionArchive.all, archiveId] as const,

    /** Query key for collection archive list with full params */
    list: (
      archiveId: string,
      collection: string,
      locale: TypedLocale,
      where: Record<string, any>,
      page: number,
      limit: number,
      sort: string | undefined,
      signedIn: boolean,
    ) => [
      ...queryKeys.collectionArchive.byArchive(archiveId),
      collection,
      locale,
      where,
      page,
      limit,
      sort ?? 'default',
      signedIn,
    ] as const,
  },
} as const
